import React, { useState, useEffect } from 'react';

/**
 * NewsletterSignup Component
 * A newsletter subscription form with validation
 */
function NewsletterSignup({ 
    placeholder = 'Enter your email',
    buttonText = 'Subscribe',
    successMessage = 'Thanks for subscribing!',
    errorMessage = 'Please enter a valid email'
}) {
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState('idle'); // idle, loading, success, error
    const [message, setMessage] = useState('');

    const validateEmail = (email) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateEmail(email)) {
            setStatus('error');
            setMessage(errorMessage);
            return;
        }

        setStatus('loading');

        // Replace this with your actual newsletter subscription logic
        // For Ghost, you might use the Members API
        try {
            // Simulated API call
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Example: POST to Ghost Members API
            // const response = await fetch('/members/api/send-magic-link/', {
            //     method: 'POST',
            //     headers: { 'Content-Type': 'application/json' },
            //     body: JSON.stringify({ email })
            // });

            setStatus('success');
            setMessage(successMessage);
            setEmail('');
        } catch (error) {
            setStatus('error');
            setMessage('Something went wrong. Please try again.');
        }
    };

    useEffect(() => {
        if (status === 'success' || status === 'error') {
            const timer = setTimeout(() => {
                setStatus('idle');
                setMessage('');
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [status]);

    const containerStyle = {
        maxWidth: '500px',
        margin: '0 auto',
        padding: '20px'
    };

    const formStyle = {
        display: 'flex',
        gap: '10px',
        marginBottom: '10px'
    };

    const inputStyle = {
        flex: 1,
        padding: '12px 16px',
        fontSize: '16px',
        border: '1px solid #ddd',
        borderRadius: '4px',
        outline: 'none',
        transition: 'border-color 0.2s'
    };

    const buttonStyle = {
        padding: '12px 24px',
        fontSize: '16px',
        fontWeight: '600',
        color: '#fff',
        backgroundColor: status === 'loading' ? '#999' : '#15171A',
        border: 'none',
        borderRadius: '4px',
        cursor: status === 'loading' ? 'not-allowed' : 'pointer',
        transition: 'background-color 0.2s',
        whiteSpace: 'nowrap'
    };

    const messageStyle = {
        padding: '10px',
        borderRadius: '4px',
        fontSize: '14px',
        textAlign: 'center',
        backgroundColor: status === 'success' ? '#d4edda' : '#f8d7da',
        color: status === 'success' ? '#155724' : '#721c24',
        border: `1px solid ${status === 'success' ? '#c3e6cb' : '#f5c6cb'}`
    };

    return (
        <div style={containerStyle}>
            <form onSubmit={handleSubmit} style={formStyle}>
                <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={placeholder}
                    disabled={status === 'loading'}
                    style={inputStyle}
                    onFocus={(e) => e.target.style.borderColor = '#15171A'}
                    onBlur={(e) => e.target.style.borderColor = '#ddd'}
                    required
                />
                <button 
                    type="submit"
                    disabled={status === 'loading'}
                    style={buttonStyle}
                    onMouseEnter={(e) => {
                        if (status !== 'loading') {
                            e.target.style.backgroundColor = 'rgba(17,24,39,0.95)';
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (status !== 'loading') {
                            e.target.style.backgroundColor = 'rgba(17,24,39,0.95)';
                        }
                    }}
                >
                    {status === 'loading' ? 'Subscribing...' : buttonText}
                </button>
            </form>
            {message && (
                <div style={messageStyle}>
                    {message}
                </div>
            )}
        </div>
    );
}

export default NewsletterSignup;
